import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useAdminLoginMutation } from '../api/index.api';
import { Button, Typography, Form, Input, message, Card } from 'antd';
import {
  LockOutlined,
  MailOutlined,
  ArrowRightOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { LoginUserAdminDto } from '../api/types/dto';
import { setCredentials, UserType } from 'sama-shared';
import samaLogo from '../../../assets/sama-logo.png';

const { Title, Text } = Typography;

/*
 * SAMA Admin Console — Centered Executive Access Portal
 * Visual Language: Deep Obsidian Titanium + Atmospheric Glowing Orbs + Official SAMA Brand Identity
 */

export const AdminLogin: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [adminLogin, { isLoading }] = useAdminLoginMutation();
  const [form] = Form.useForm();
  const [btnHover, setBtnHover] = useState(false);

  const handleLogin = async (values: LoginUserAdminDto) => {
    try {
      const response = await adminLogin(values).unwrap();
      const userType = response.type;

      if (userType !== UserType.admin) {
        message.error(
          'Acceso denegado: este portal es exclusivo para administradores de la plataforma.'
        );
        return;
      }

      const partner = response.partner
        ? {
            id: response.partner.id,
            name: response.partner.name,
            industry: response.partner.industry,
            exchangeRateCode: response.partner.exchangeRateCode || ('USD' as const),
          }
        : null;
      const allowedStores = response.allowedStores || [];

      dispatch(
        setCredentials({
          token: response.token,
          partner,
          allowedStores,
          type: userType,
          isSuperadmin: response.isSuperadmin,
        })
      );

      message.success('Sesión iniciada correctamente. Bienvenido a la consola de administración.');
      navigate('/admin');
    } catch (err: any) {
      console.error('Admin login error:', err);
      message.error(
        err?.data?.message || 'Error de autenticación. Verifica las credenciales de administrador.'
      );
    }
  };

  return (
    <>
      <style>{`
        @keyframes sama-admin-drift {
          0%, 100% { transform: translate3d(0, 0, 0) scale(1); }
          50% { transform: translate3d(28px, -22px, 0) scale(1.14); }
        }
        @keyframes sama-admin-pulse {
          0%, 100% { opacity: 0.8; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.05); }
        }
        .sama-admin-input .ant-input-affix-wrapper {
          transition: all 0.2s ease;
        }
        .sama-admin-input .ant-input-affix-wrapper:hover {
          border-color: rgba(255, 108, 0, 0.45) !important;
        }
        .sama-admin-input .ant-input-affix-wrapper-focused {
          border-color: #ff6c00 !important;
          box-shadow: 0 0 0 3px rgba(255, 108, 0, 0.20) !important;
        }
      `}</style>

      <div
        style={{
          minHeight: '100vh',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden',
          padding: '32px 16px',
          background:
            'radial-gradient(1100px 600px at 50% 15%, rgba(255, 108, 0, 0.14), transparent 60%), radial-gradient(900px 600px at 50% 85%, rgba(14, 165, 233, 0.06), transparent 60%), #080b11',
        }}
      >
        {/* Orbes de luz atmosféricos flotantes */}
        <div
          aria-hidden
          style={{
            position: 'absolute',
            width: 480,
            height: 480,
            left: '12%',
            top: '8%',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255, 108, 0, 0.20), transparent 65%)',
            filter: 'blur(34px)',
            animation: 'sama-admin-drift 12s ease-in-out infinite',
            pointerEvents: 'none',
          }}
        />
        <div
          aria-hidden
          style={{
            position: 'absolute',
            width: 440,
            height: 440,
            right: '12%',
            bottom: '10%',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(245, 158, 11, 0.15), transparent 65%)',
            filter: 'blur(34px)',
            animation: 'sama-admin-drift 14s ease-in-out infinite reverse',
            pointerEvents: 'none',
          }}
        />

        {/* Card Centrada */}
        <Card
          variant="borderless"
          style={{
            width: '100%',
            maxWidth: 440,
            borderRadius: 22,
            background: 'rgba(17, 23, 36, 0.88)',
            backdropFilter: 'blur(16px)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            boxShadow:
              '0 30px 80px -20px rgba(0, 0, 0, 0.85), 0 0 50px -10px rgba(255, 108, 0, 0.18)',
            position: 'relative',
            zIndex: 1,
          }}
          styles={{ body: { padding: '40px 34px' } }}
        >
          {/* Header con Logo Oficial de SAMA */}
          <div style={{ textAlign: 'center', marginBottom: 28 }}>
            <img
              src={samaLogo}
              alt="SAMA Logo"
              style={{
                height: 54,
                objectFit: 'contain',
                margin: '0 auto 18px auto',
                display: 'block',
                filter: 'drop-shadow(0 4px 16px rgba(255, 108, 0, 0.35))',
              }}
            />

            {/* Badge de Consola Administrativa */}
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 7,
                marginBottom: 10,
                padding: '4px 12px',
                borderRadius: 20,
                background: 'rgba(255, 108, 0, 0.12)',
                border: '1px solid rgba(255, 108, 0, 0.28)',
              }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  background: '#ff7a1a',
                  display: 'inline-block',
                }}
              />
              <Text
                style={{
                  color: '#ff8c33',
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                }}
              >
                Consola Administrativa Global
              </Text>
            </div>

            <Title
              level={2}
              style={{
                color: '#ffffff',
                margin: '4px 0 6px',
                fontSize: 24,
                fontWeight: 800,
                letterSpacing: '-0.02em',
              }}
            >
              Bienvenido de vuelta
            </Title>
            <Text style={{ color: 'rgba(255, 255, 255, 0.52)', fontSize: 13.5, display: 'block' }}>
              Ingresa tus credenciales maestras autorizadas.
            </Text>
          </div>

          {/* Formulario */}
          <Form
            form={form}
            name="admin-login"
            initialValues={{ remember: true }}
            onFinish={handleLogin}
            size="large"
            layout="vertical"
            requiredMark={false}
          >
            <Form.Item
              label={
                <span
                  style={{
                    color: 'rgba(255, 255, 255, 0.85)',
                    fontSize: 13,
                    fontWeight: 600,
                  }}
                >
                  Correo Electrónico
                </span>
              }
              name="email"
              rules={[
                { required: true, message: 'Por favor ingresa tu correo de administrador' },
                { type: 'email', message: 'Ingresa un correo electrónico válido' },
              ]}
              style={{ marginBottom: 20 }}
            >
              <div className="sama-admin-input">
                <Input
                  prefix={<MailOutlined style={{ color: '#ff8c33', marginRight: 8 }} />}
                  placeholder="admin@sama.com"
                  autoComplete="email"
                  style={{
                    background: '#090d15',
                    border: '1px solid rgba(255, 255, 255, 0.12)',
                    color: '#ffffff',
                    borderRadius: 10,
                    height: 48,
                    fontSize: 14,
                  }}
                />
              </div>
            </Form.Item>

            <Form.Item
              label={
                <span
                  style={{
                    color: 'rgba(255, 255, 255, 0.85)',
                    fontSize: 13,
                    fontWeight: 600,
                  }}
                >
                  Contraseña
                </span>
              }
              name="password"
              rules={[{ required: true, message: 'Por favor ingresa tu contraseña' }]}
              style={{ marginBottom: 28 }}
            >
              <div className="sama-admin-input">
                <Input.Password
                  prefix={<LockOutlined style={{ color: '#ff8c33', marginRight: 8 }} />}
                  placeholder="••••••••••••"
                  autoComplete="current-password"
                  style={{
                    background: '#090d15',
                    border: '1px solid rgba(255, 255, 255, 0.12)',
                    color: '#ffffff',
                    borderRadius: 10,
                    height: 48,
                    fontSize: 14,
                  }}
                />
              </div>
            </Form.Item>

            {/* Botón de Submit Siempre Visible, Vibrante y con Glow */}
            <Form.Item style={{ marginBottom: 16 }}>
              <Button
                type="primary"
                htmlType="submit"
                block
                loading={isLoading}
                onMouseEnter={() => setBtnHover(true)}
                onMouseLeave={() => setBtnHover(false)}
                style={{
                  height: 50,
                  fontSize: 15,
                  fontWeight: 700,
                  borderRadius: 10,
                  background: btnHover
                    ? 'linear-gradient(135deg, #ff6c00 0%, #ff8c33 50%, #ffb833 100%)'
                    : 'linear-gradient(135deg, #ff5a00 0%, #ff7a1a 50%, #ffb020 100%)',
                  border: 'none',
                  boxShadow: btnHover
                    ? '0 12px 32px -4px rgba(255, 90, 0, 0.7), inset 0 1px 0 rgba(255, 255, 255, 0.4)'
                    : '0 8px 24px -4px rgba(255, 90, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.3)',
                  transform: btnHover ? 'translateY(-1px)' : 'translateY(0)',
                  transition: 'all 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
                  color: '#ffffff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  cursor: 'pointer',
                }}
              >
                <span>Iniciar Sesión</span>
                <ArrowRightOutlined style={{ fontSize: 14 }} />
              </Button>
            </Form.Item>
          </Form>

          {/* Nota de Seguridad */}
          <div
            style={{
              marginTop: 16,
              padding: '10px 14px',
              borderRadius: 8,
              background: 'rgba(255, 255, 255, 0.02)',
              border: '1px solid rgba(255, 255, 255, 0.05)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
            }}
          >
            <Text
              style={{
                color: 'rgba(255, 255, 255, 0.42)',
                fontSize: 11.5,
                textAlign: 'center',
              }}
            >
              🔒 Sesión encriptada con TLS 1.3 · Acceso auditado por SAMA Core
            </Text>
          </div>
        </Card>

        {/* Footer institucional */}
        <div style={{ marginTop: 24, textAlign: 'center', position: 'relative', zIndex: 1 }}>
          <Text style={{ color: 'rgba(255, 255, 255, 0.3)', fontSize: 12 }}>
            © 2026 SAMA Platform Inc. Todos los derechos reservados.
          </Text>
        </div>
      </div>
    </>
  );
};

export default AdminLogin;

